import React, { useEffect, useState, useRef } from 'react';
import { Text, TouchableOpacity, View, StyleSheet, Image, TextInput, Switch, ScrollView } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';

import { msgStr } from '../../../common/constants/Message';
import { API_URL } from '../../../common/constants/AppConstants';
import { useAlertModal, useConfirmModal } from '../../../common/hooks';
import { BasicLayout, CommonContainer } from '../../../common/components/CustomLayout';
import { BOHButton } from '../../../common/components/bohtoolbar';
import { updateProfileWithLogo, getProfileSecrets } from '../../../api/Settings';
import { getBrandsList } from '../../../api/Price';
import { fetchProfileSettings } from '../../../redux/slices/profileSettingsSlice';

const GeneralSettings = ({ navigation, openInventory }) => {

  const dispatch = useDispatch<AppDispatch>();
  const { settings, loading, error } = useSelector((state: RootState) => state.profileSettings);

  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();

  // State variables for logo and location
  const [logo, setLogo] = useState(null);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');

  // State variables for closing time
  const [closingTime, setClosingTime] = useState('');
  const [isClosingTimeEnabled, setIsClosingTimeEnabled] = useState(false);

  // State variables for buffer/clean time
  const [bufferHours, setBufferHours] = useState('');
  const [bufferMinutes, setBufferMinutes] = useState('');
  const [isBufferTimeEnabled, setIsBufferTimeEnabled] = useState(false);

  // State variables for tracking tags visibility (software only)
  const [isTrackingTagsEnabled, setIsTrackingTagsEnabled] = useState(false);

  // State variable for allowing brand change on reservations (system wide)
  const [isBrandChangeEnabled, setIsBrandChangeEnabled] = useState(false);

  // State variables for app configuration
  const [appEmail, setAppEmail] = useState('');
  const [appName, setAppName] = useState('');
  
  // SendGrid API configuration
  const [sendgridApiKey, setSendgridApiKey] = useState('');
  const [showSendgridApiKey, setShowSendgridApiKey] = useState(false);
  const [isSendGridEnabled, setIsSendGridEnabled] = useState(false);
  
  // Twilio SMS configuration
  const [twilioAccountSid, setTwilioAccountSid] = useState('');
  const [twilioAuthToken, setTwilioAuthToken] = useState('');
  const [twilioNumber, setTwilioNumber] = useState('');
  const [showTwilioAuthToken, setShowTwilioAuthToken] = useState(false);
  const [isTwilioEnabled, setIsTwilioEnabled] = useState(false);

  // New order alert settings
  const [isNewOrderEmailAlertEnabled, setIsNewOrderEmailAlertEnabled] = useState(false);
  const [newOrderEmailAlertRecipients, setNewOrderEmailAlertRecipients] = useState('');
  const [isNewOrderSmsAlertEnabled, setIsNewOrderSmsAlertEnabled] = useState(false);
  const [newOrderSmsAlertRecipients, setNewOrderSmsAlertRecipients] = useState('');

  // New order alert brand filter (empty means all brands)
  const [brandsList, setBrandsList] = useState<any[]>([]);
  const [selectedAlertBrandIds, setSelectedAlertBrandIds] = useState<number[]>([]);
  
  // Stripe keys
  const [stripeLivePublicKey, setStripeLivePublicKey] = useState('');
  const [stripeLiveSecretKey, setStripeLiveSecretKey] = useState('');
  const [stripeTestPublicKey, setStripeTestPublicKey] = useState('');
  const [stripeTestSecretKey, setStripeTestSecretKey] = useState('');
  const [isStripeLiveEnabled, setIsStripeLiveEnabled] = useState(false);
  const [showLiveSecretKey, setShowLiveSecretKey] = useState(false);
  const [showTestSecretKey, setShowTestSecretKey] = useState(false);
  const [hasFetchedSecrets, setHasFetchedSecrets] = useState(false);
  // Tracks the plaintext values most recently fetched from the server so we
  // can avoid resending (and thus re-encrypting) a secret that the user only
  // revealed via the eye-toggle but did not actually change.
  const [originalSecrets, setOriginalSecrets] = useState<{
    stripe_live_secret_key?: string;
    stripe_test_secret_key?: string;
    sendgrid_api_key?: string;
    twilio_auth_token?: string;
  }>({});

  // State variables for setting IDs
  const [logoSettingId, setLogoSettingId] = useState(null);
  const [latitudeSettingId, setLatitudeSettingId] = useState(null);
  const [longitudeSettingId, setLongitudeSettingId] = useState(null);
  const [closingTimeSettingId, setClosingTimeSettingId] = useState(null);
  const [bufferTimeSettingId, setBufferTimeSettingId] = useState(null);
  const [trackingTagsSettingId, setTrackingTagsSettingId] = useState(null);
  const [brandChangeSettingId, setBrandChangeSettingId] = useState(null);
  const [appEmailSettingId, setAppEmailSettingId] = useState(null);
  const [appNameSettingId, setAppNameSettingId] = useState(null);
  const [stripeLivePublicKeyId, setStripeLivePublicKeyId] = useState(null);
  const [stripeLiveSecretKeyId, setStripeLiveSecretKeyId] = useState(null);
  const [stripeTestPublicKeyId, setStripeTestPublicKeyId] = useState(null);
  const [stripeTestSecretKeyId, setStripeTestSecretKeyId] = useState(null);
  const [stripeLiveEnabledId, setStripeLiveEnabledId] = useState(null);
  
  // SendGrid API setting IDs
  const [sendgridApiKeySettingId, setSendgridApiKeySettingId] = useState(null);
  
  // Twilio SMS setting IDs
  const [twilioAccountSidSettingId, setTwilioAccountSidSettingId] = useState(null);
  const [twilioAuthTokenSettingId, setTwilioAuthTokenSettingId] = useState(null);
  const [twilioNumberSettingId, setTwilioNumberSettingId] = useState(null);
  const [sendGridEnabledId, setSendGridEnabledId] = useState(null);
  const [twilioEnabledId, setTwilioEnabledId] = useState(null);

  // New order alert setting IDs
  const [newOrderEmailAlertEnabledId, setNewOrderEmailAlertEnabledId] = useState<number | null>(null);
  const [newOrderEmailAlertRecipientsId, setNewOrderEmailAlertRecipientsId] = useState<number | null>(null);
  const [newOrderSmsAlertEnabledId, setNewOrderSmsAlertEnabledId] = useState<number | null>(null);
  const [newOrderSmsAlertRecipientsId, setNewOrderSmsAlertRecipientsId] = useState<number | null>(null);
  const [newOrderAlertBrandIdsId, setNewOrderAlertBrandIdsId] = useState<number | null>(null);

  // Reference to file input
  const fileInputRef = useRef(null);

  // State to store the selected file
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Function to handle file selection for logo
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogo(e.target.result);
        // Store the file for later upload
        setSelectedFile(file);
      };
      reader.readAsDataURL(file);
    } else if (file) {
      showAlert('error', 'Please select an image file');
    }
  };

  console.log("Updated code is working")

  // Function to trigger file input click
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Helper to fetch all secret values from the server
  const fetchSecretsIfNeeded = (callback?: () => void) => {
    if (!hasFetchedSecrets) {
      getProfileSecrets((jsonRes, status, _err) => {
        if (status === 200) {
          if (jsonRes.stripe_live_secret_key) setStripeLiveSecretKey(jsonRes.stripe_live_secret_key);
          if (jsonRes.stripe_test_secret_key) setStripeTestSecretKey(jsonRes.stripe_test_secret_key);
          if (jsonRes.sendgrid_api_key) setSendgridApiKey(jsonRes.sendgrid_api_key);
          if (jsonRes.twilio_auth_token) setTwilioAuthToken(jsonRes.twilio_auth_token);
          setOriginalSecrets({
            stripe_live_secret_key: jsonRes.stripe_live_secret_key,
            stripe_test_secret_key: jsonRes.stripe_test_secret_key,
            sendgrid_api_key: jsonRes.sendgrid_api_key,
            twilio_auth_token: jsonRes.twilio_auth_token,
          });
          setHasFetchedSecrets(true);
        }
        if (callback) callback();
      });
    } else if (callback) {
      callback();
    }
  };

  // Function to handle toggle live secret
  const handleToggleLiveSecret = () => {
    if (stripeLiveSecretKey === '********') fetchSecretsIfNeeded();
    setShowLiveSecretKey(!showLiveSecretKey);
  };

  // Function to handle toggle test secret
  const handleToggleTestSecret = () => {
    if (stripeTestSecretKey === '********') fetchSecretsIfNeeded();
    setShowTestSecretKey(!showTestSecretKey);
  };

  // Function to handle toggle SendGrid API key visibility
  const handleToggleSendgridApiKey = () => {
    if (sendgridApiKey === '********') fetchSecretsIfNeeded();
    setShowSendgridApiKey(!showSendgridApiKey);
  };

  // Function to handle toggle Twilio auth token
  const handleToggleTwilioAuthToken = () => {
    if (twilioAuthToken === '********') fetchSecretsIfNeeded();
    setShowTwilioAuthToken(!showTwilioAuthToken);
  };

  // Toggle a brand in/out of the new order alert filter
  const toggleAlertBrand = (brandId: number) => {
    setSelectedAlertBrandIds((prev) =>
      prev.includes(brandId) ? prev.filter((id) => id !== brandId) : [...prev, brandId]
    );
  };

  // Function to save profile (combines logo upload and coordinates update)
  const saveProfile = async () => {
    // Validate coordinates if provided
    let lat = null;
    let lng = null;

    if (latitude || longitude) {
      lat = parseFloat(latitude);
      lng = parseFloat(longitude);

      if ((latitude && isNaN(lat)) || (longitude && isNaN(lng)) ||
        (lat && (lat < -90 || lat > 90)) || (lng && (lng < -180 || lng > 180))) {
        showAlert('error', 'Please enter valid coordinates. Latitude must be between -90 and 90, and longitude between -180 and 180.');
        return;
      }
    }

    setIsLoading(true);
    try {
      // Create FormData for file upload and settings data
      const formData = new FormData();

      // Create settings array for the profile data
      const settingsArray = [];

      // Add logo setting if a file is selected
      if (selectedFile) {
        formData.append('img', selectedFile);
        settingsArray.push({
          id: logoSettingId, // Include ID if available
          item_key: 'logo',
          item_value: 'placeholder', // This will be replaced by the server with the file URL
          item_type: 'image'
        });
      }

      // Add latitude setting if available
      if (lat !== null) {
        settingsArray.push({
          id: latitudeSettingId, // Include ID if available
          item_key: 'latitude',
          item_value: lat.toString(),
          item_type: 'coordinate'
        });
      }

      // Add longitude setting if available
      if (lng !== null) {
        settingsArray.push({
          id: longitudeSettingId, // Include ID if available
          item_key: 'longitude',
          item_value: lng.toString(),
          item_type: 'coordinate'
        });
      }

      // Add closing time setting
      settingsArray.push({
        id: closingTimeSettingId, // Include ID if available
        item_key: 'closing_time',
        item_value: isClosingTimeEnabled ? closingTime : '',
        item_type: 'time'
      });

      // Add buffer/clean time setting
      const minutesPart = Math.min(59, Math.max(0, parseInt(bufferMinutes || '0') || 0));
      const hoursPart = Math.max(0, parseInt(bufferHours || '0') || 0);
      const totalMinutes = (hoursPart * 60) + minutesPart;
      settingsArray.push({
        id: bufferTimeSettingId, // Include ID if available
        item_key: 'buffer_clean_time',
        item_value: isBufferTimeEnabled ? totalMinutes.toString() : '',
        item_type: 'duration'
      });

      // Add tracking tags toggle
      settingsArray.push({
        id: trackingTagsSettingId,
        item_key: 'tracking_tags_enabled',
        item_value: isTrackingTagsEnabled ? 'true' : 'false',
        item_type: 'boolean'
      });

      // Add allow reservation brand change toggle
      settingsArray.push({
        id: brandChangeSettingId,
        item_key: 'allow_reservation_brand_change',
        item_value: isBrandChangeEnabled ? 'true' : 'false',
        item_type: 'boolean'
      });

      // Add app email setting
      if (appEmail) {
        settingsArray.push({
          id: appEmailSettingId,
          item_key: 'app_email',
          item_value: appEmail,
          item_type: 'text'
        });
      }

      // Add app name setting
      if (appName) {
        settingsArray.push({
          id: appNameSettingId,
          item_key: 'app_name',
          item_value: appName,
          item_type: 'text'
        });
      }

      // Add Stripe keys
      if (stripeLivePublicKey) {
        settingsArray.push({
          id: stripeLivePublicKeyId,
          item_key: 'stripe_live_public_key',
          item_value: stripeLivePublicKey,
          item_type: 'text'
        });
      }
      if (
        stripeLiveSecretKey &&
        stripeLiveSecretKey !== '********' &&
        stripeLiveSecretKey !== originalSecrets.stripe_live_secret_key
      ) {
        settingsArray.push({
          id: stripeLiveSecretKeyId,
          item_key: 'stripe_live_secret_key',
          item_value: stripeLiveSecretKey,
          item_type: 'text'
        });
      }
      if (stripeTestPublicKey) {
        settingsArray.push({
          id: stripeTestPublicKeyId,
          item_key: 'stripe_test_public_key',
          item_value: stripeTestPublicKey,
          item_type: 'text'
        });
      }
      if (
        stripeTestSecretKey &&
        stripeTestSecretKey !== '********' &&
        stripeTestSecretKey !== originalSecrets.stripe_test_secret_key
      ) {
        settingsArray.push({
          id: stripeTestSecretKeyId,
          item_key: 'stripe_test_secret_key',
          item_value: stripeTestSecretKey,
          item_type: 'text'
        });
      }

      settingsArray.push({
        id: stripeLiveEnabledId,
        item_key: 'stripe_live_enabled',
        item_value: isStripeLiveEnabled ? 'true' : 'false',
        item_type: 'boolean'
      });

      // Add SendGrid API key setting
      if (
        sendgridApiKey &&
        sendgridApiKey !== '********' &&
        sendgridApiKey !== originalSecrets.sendgrid_api_key
      ) {
        settingsArray.push({
          id: sendgridApiKeySettingId,
          item_key: 'sendgrid_api_key',
          item_value: sendgridApiKey,
          item_type: 'secret'
        });
      }

      // Add Twilio SMS settings
      if (twilioAccountSid) {
        settingsArray.push({
          id: twilioAccountSidSettingId,
          item_key: 'twilio_account_sid',
          item_value: twilioAccountSid,
          item_type: 'text'
        });
      }
      if (
        twilioAuthToken &&
        twilioAuthToken !== '********' &&
        twilioAuthToken !== originalSecrets.twilio_auth_token
      ) {
        settingsArray.push({
          id: twilioAuthTokenSettingId,
          item_key: 'twilio_auth_token',
          item_value: twilioAuthToken,
          item_type: 'secret'
        });
      }
      if (twilioNumber) {
        settingsArray.push({
          id: twilioNumberSettingId,
          item_key: 'twilio_number',
          item_value: twilioNumber,
          item_type: 'text'
        });
      }

      // Add SendGrid and Twilio enabled toggles
      settingsArray.push({
        id: sendGridEnabledId,
        item_key: 'sendgrid_enabled',
        item_value: isSendGridEnabled ? 'true' : 'false',
        item_type: 'boolean'
      });

      settingsArray.push({
        id: twilioEnabledId,
        item_key: 'twilio_enabled',
        item_value: isTwilioEnabled ? 'true' : 'false',
        item_type: 'boolean'
      });

      // Add new order alert toggles and recipient fields
      settingsArray.push({
        id: newOrderEmailAlertEnabledId,
        item_key: 'new_order_email_alert_enabled',
        item_value: isNewOrderEmailAlertEnabled ? 'true' : 'false',
        item_type: 'boolean'
      });

      settingsArray.push({
        id: newOrderEmailAlertRecipientsId,
        item_key: 'new_order_email_alert_recipients',
        item_value: newOrderEmailAlertRecipients,
        item_type: 'text'
      });

      settingsArray.push({
        id: newOrderSmsAlertEnabledId,
        item_key: 'new_order_sms_alert_enabled',
        item_value: isNewOrderSmsAlertEnabled ? 'true' : 'false',
        item_type: 'boolean'
      });

      settingsArray.push({
        id: newOrderSmsAlertRecipientsId,
        item_key: 'new_order_sms_alert_recipients',
        item_value: newOrderSmsAlertRecipients,
        item_type: 'text'
      });

      settingsArray.push({
        id: newOrderAlertBrandIdsId,
        item_key: 'new_order_alert_brand_ids',
        item_value: selectedAlertBrandIds.join(','),
        item_type: 'text'
      });

      // Append the settings array to the form data
      formData.append('settings', JSON.stringify(settingsArray));

      updateProfileWithLogo(formData, (jsonRes, status, _err) => {
        setIsLoading(false);

        switch (status) {
          case 200:
          case 201:
            showAlert('success', jsonRes.message);
            // Clear selected file after successful upload
            setSelectedFile(null);
            // Reset secrets cache so eye-toggle re-fetches real values after refresh
            setHasFetchedSecrets(false);
            setOriginalSecrets({});
            // Reset show/hide state for all secret fields
            setShowLiveSecretKey(false);
            setShowTestSecretKey(false);
            setShowSendgridApiKey(false);
            setShowTwilioAuthToken(false);
            // Refresh profile settings after successful update
            dispatch(fetchProfileSettings(undefined));
            break;
          case 409:
            showAlert('error', jsonRes.error);
            break;
          default:
            if (jsonRes && jsonRes.error) {
              showAlert('error', jsonRes.error);
            } else {
              showAlert('error', 'Failed to update profile');
            }
            break;
        }
      });
    } catch (err) {
      console.error('Profile update error:', err);
      setIsLoading(false);
      showAlert('error', 'Failed to update profile');
    }
  };

  // Fetch profile settings from Redux
  useEffect(() => {
    // Fetch profile settings using Redux
    dispatch(fetchProfileSettings(undefined));
  }, [dispatch]);

  // Fetch brands list for the new-order alert brand selector
  useEffect(() => {
    getBrandsList({}, (jsonRes, status) => {
      if (status === 200 && jsonRes) {
        setBrandsList(jsonRes);
      }
    });
  }, []);

  // Show alert only when the error value itself changes
  useEffect(() => {
    if (error) {
      showAlert('error', `Failed to fetch profile settings: ${error}`);
    }
  }, [error]);

  // Effect to handle profile settings data when it changes
  useEffect(() => {
    // Process settings if needed
    if (settings && settings.length > 0) {
      // Process settings data
      settings.forEach(setting => {
        // Handle different setting types
        switch (setting.item_type) {
          case 'coordinate':
            if (setting.item_key === 'latitude') {
              setLatitude(setting.item_value);
              setLatitudeSettingId(setting.id); // Store the ID
            } else if (setting.item_key === 'longitude') {
              setLongitude(setting.item_value);
              setLongitudeSettingId(setting.id); // Store the ID
            }
            break;
          case 'image':
            if (setting.item_key === 'logo') {
              setLogo(setting.item_value);
              setLogoSettingId(setting.id); // Store the ID
            }
            break;
          case 'time':
            if (setting.item_key === 'closing_time') {
              setClosingTime(setting.item_value);
              setClosingTimeSettingId(setting.id); // Store the ID
              setIsClosingTimeEnabled(setting.item_value !== null && setting.item_value.trim() !== '');
            }
            break;
          case 'duration':
            if (setting.item_key === 'buffer_clean_time') {
              const totalMins = parseInt(setting.item_value);
              if (!isNaN(totalMins)) {
                setBufferHours(Math.floor(totalMins / 60).toString());
                setBufferMinutes((totalMins % 60).toString());
              } else {
                setBufferHours('');
                setBufferMinutes('');
              }
              setBufferTimeSettingId(setting.id); // Store the ID
              setIsBufferTimeEnabled(setting.item_value !== null && setting.item_value.trim() !== '');
            }
            break;
          case 'boolean':
            if (setting.item_key === 'tracking_tags_enabled') {
              const normalized = `${setting.item_value}`.toLowerCase();
              setIsTrackingTagsEnabled(normalized === 'true');
              setTrackingTagsSettingId(setting.id);
            } else if (setting.item_key === 'allow_reservation_brand_change') {
              const normalized = `${setting.item_value}`.toLowerCase();
              setIsBrandChangeEnabled(normalized === 'true');
              setBrandChangeSettingId(setting.id);
            } else if (setting.item_key === 'stripe_live_enabled') {
              const normalized = `${setting.item_value}`.toLowerCase();
              setIsStripeLiveEnabled(normalized === 'true');
              setStripeLiveEnabledId(setting.id);
            } else if (setting.item_key === 'sendgrid_enabled') {
              const normalized = `${setting.item_value}`.toLowerCase();
              setIsSendGridEnabled(normalized === 'true');
              setSendGridEnabledId(setting.id);
            } else if (setting.item_key === 'twilio_enabled') {
              const normalized = `${setting.item_value}`.toLowerCase();
              setIsTwilioEnabled(normalized === 'true');
              setTwilioEnabledId(setting.id);
            } else if (setting.item_key === 'new_order_email_alert_enabled') {
              const normalized = `${setting.item_value}`.toLowerCase();
              setIsNewOrderEmailAlertEnabled(normalized === 'true');
              setNewOrderEmailAlertEnabledId(setting.id ?? null);
            } else if (setting.item_key === 'new_order_sms_alert_enabled') {
              const normalized = `${setting.item_value}`.toLowerCase();
              setIsNewOrderSmsAlertEnabled(normalized === 'true');
              setNewOrderSmsAlertEnabledId(setting.id ?? null);
            }
            break;
          case 'text':
            if (setting.item_key === 'app_email') {
              setAppEmail(setting.item_value);
              setAppEmailSettingId(setting.id);
            } else if (setting.item_key === 'app_name') {
              setAppName(setting.item_value);
              setAppNameSettingId(setting.id);
            } else if (setting.item_key === 'stripe_live_public_key') {
              setStripeLivePublicKey(setting.item_value);
              setStripeLivePublicKeyId(setting.id);
            } else if (setting.item_key === 'stripe_live_secret_key') {
              setStripeLiveSecretKey(setting.item_value);
              setStripeLiveSecretKeyId(setting.id);
            } else if (setting.item_key === 'stripe_test_public_key') {
              setStripeTestPublicKey(setting.item_value);
              setStripeTestPublicKeyId(setting.id);
            } else if (setting.item_key === 'stripe_test_secret_key') {
              setStripeTestSecretKey(setting.item_value);
              setStripeTestSecretKeyId(setting.id);
            } else if (setting.item_key === 'twilio_account_sid') {
              setTwilioAccountSid(setting.item_value);
              setTwilioAccountSidSettingId(setting.id);
            } else if (setting.item_key === 'twilio_number') {
              setTwilioNumber(setting.item_value);
              setTwilioNumberSettingId(setting.id);
            } else if (setting.item_key === 'new_order_email_alert_recipients') {
              setNewOrderEmailAlertRecipients(setting.item_value || '');
              setNewOrderEmailAlertRecipientsId(setting.id ?? null);
            } else if (setting.item_key === 'new_order_sms_alert_recipients') {
              setNewOrderSmsAlertRecipients(setting.item_value || '');
              setNewOrderSmsAlertRecipientsId(setting.id ?? null);
            } else if (setting.item_key === 'new_order_alert_brand_ids') {
              const brandIds = (setting.item_value || '')
                .split(/[,;\n]+/)
                .map((v) => parseInt(v.trim(), 10))
                .filter((v) => !isNaN(v));
              setSelectedAlertBrandIds(brandIds);
              setNewOrderAlertBrandIdsId(setting.id ?? null);
            }
            break;
          case 'secret':
            if (setting.item_key === 'sendgrid_api_key') {
              setSendgridApiKey(setting.item_value);
              setSendgridApiKeySettingId(setting.id);
            } else if (setting.item_key === 'twilio_auth_token') {
              setTwilioAuthToken(setting.item_value);
              setTwilioAuthTokenSettingId(setting.id);
            }
            break;
        }
      });
    }
  }, [settings]);

  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => {
        openInventory(null);
      }}
      screenName={'General Settings'}
      containerStyle={{ flex: 1, height: '100%' }}
    >
      <CommonContainer horizontal={false}>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 32, flexGrow: 1 }}>
          {/* Logo Upload Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Company Logo</Text>
            <View style={styles.logoContainer}>
              {logo ? (
                <Image
                  source={{ uri: logo.startsWith('data:') ? logo : API_URL + logo }}
                  style={styles.logo}
                />) : (
                <View style={styles.placeholderLogo}>
                  <FontAwesome5 name="building" size={40} color="#ccc" />
                </View>
              )}
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <BOHButton
                label={'Select Logo'}
                onPress={triggerFileInput}
              />
            </View>
          </View>

          {/* Location Coordinates Section - Improved UI */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Location Coordinates</Text>
            <View style={styles.coordinatesContainer}>
              <View style={styles.inputRow}>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Latitude:</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={latitude}
                      onChangeText={setLatitude}
                      placeholder="37.7749"
                      placeholderTextColor="#AAAAAA"
                      keyboardType="numeric"
                    />
                    <Text style={styles.inputUnit}>°N</Text>
                  </View>
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Longitude:</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={longitude}
                      onChangeText={setLongitude}
                      placeholder="-122.4194"
                      placeholderTextColor="#AAAAAA"
                      keyboardType="numeric"
                    />
                    <Text style={styles.inputUnit}>°E</Text>
                  </View>
                </View>
              </View>
              <View style={styles.helpText}>
                <FontAwesome5 name="info-circle" size={14} color="#666" />
                <Text style={styles.helpTextContent}>
                  Enter coordinates in decimal format (e.g., 37.7749, -122.4194)
                </Text>
              </View>
              <View style={styles.buttonRow}>
                <BOHButton
                  label={'Get Current Location'}
                  onPress={() => {
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition(
                        (position) => {
                          setLatitude(position.coords.latitude.toFixed(6));
                          setLongitude(position.coords.longitude.toFixed(6));
                          showAlert('success', 'Current location retrieved successfully!');
                        },
                        (error) => {
                          showAlert('error', 'Error getting location: ' + error.message);
                        }
                      );
                    } else {
                      showAlert('error', 'Geolocation is not supported by this browser.');
                    }
                  }}
                />
              </View>
            </View>
          </View>

          {/* Closing Time Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Employee Auto clock out</Text>
            <View style={styles.closingTimeContainer}>
              <View style={styles.inputRow}>
                <View style={{ width: '100%' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text style={styles.inputLabel}>Time:</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={{ marginRight: 8, color: '#6c757d', fontWeight: isClosingTimeEnabled ? '400' : '600' }}>OFF</Text>
                      <Switch
                        value={isClosingTimeEnabled}
                        onValueChange={(newValue) => {
                          setIsClosingTimeEnabled(newValue);
                          if (!newValue) {
                            setClosingTime('');
                          }
                        }}
                        trackColor={{ false: '#6c757d', true: '#2e96e1' }}
                        thumbColor={isClosingTimeEnabled ? '#ffc107' : '#f8f9fa'}
                      />
                      <Text style={{ marginLeft: 8, color: '#6c757d', fontWeight: isClosingTimeEnabled ? '600' : '400' }}>ON</Text>
                    </View>
                  </View>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={[styles.input, !isClosingTimeEnabled && { backgroundColor: '#f0f0f0' }]}
                      value={closingTime}
                      onChangeText={setClosingTime}
                      placeholder="e.g., 17:00"
                      placeholderTextColor="#AAAAAA"
                      editable={isClosingTimeEnabled}
                    />
                  </View>
                </View>
              </View>
              <View style={styles.helpText}>
                <FontAwesome5 name="info-circle" size={14} color="#666" />
                <Text style={styles.helpTextContent}>
                  Enter time in 24-hour format (e.g., 17:00 for 5:00 PM)
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Buffer/Clean time</Text>
            <View style={styles.closingTimeContainer}>
              <View style={styles.inputRow}>
                <View style={{ width: '100%' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text style={styles.inputLabel}>Duration:</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={{ marginRight: 8, color: '#6c757d', fontWeight: isBufferTimeEnabled ? '400' : '600' }}>OFF</Text>
                      <Switch
                        value={isBufferTimeEnabled}
                        onValueChange={(newValue) => {
                          setIsBufferTimeEnabled(newValue);
                          if (!newValue) {
                            setBufferHours('');
                            setBufferMinutes('');
                          }
                        }}
                        trackColor={{ false: '#6c757d', true: '#2e96e1' }}
                        thumbColor={isBufferTimeEnabled ? '#ffc107' : '#f8f9fa'}
                      />
                      <Text style={{ marginLeft: 8, color: '#6c757d', fontWeight: isBufferTimeEnabled ? '600' : '400' }}>ON</Text>
                    </View>
                  </View>

                  <View style={styles.inputRow}>
                    <View style={[styles.inputGroup, { width: '48%' }]}>
                      <Text style={styles.inputLabel}>Hours:</Text>
                      <View style={styles.inputWithIcon}>
                        <TextInput
                          style={[styles.input, !isBufferTimeEnabled && { backgroundColor: '#f0f0f0' }]}
                          value={bufferHours}
                          onChangeText={(val) => setBufferHours(val.replace(/[^0-9]/g, ''))}
                          placeholder="0"
                          placeholderTextColor="#AAAAAA"
                          keyboardType="numeric"
                          editable={isBufferTimeEnabled}
                        />
                      </View>
                    </View>
                    <View style={[styles.inputGroup, { width: '48%' }]}>
                      <Text style={styles.inputLabel}>Minutes:</Text>
                      <View style={styles.inputWithIcon}>
                        <TextInput
                          style={[styles.input, !isBufferTimeEnabled && { backgroundColor: '#f0f0f0' }]}
                          value={bufferMinutes}
                          onChangeText={(val) => setBufferMinutes(val.replace(/[^0-9]/g, ''))}
                          placeholder="0"
                          placeholderTextColor="#AAAAAA"
                          keyboardType="numeric"
                          editable={isBufferTimeEnabled}
                        />
                      </View>
                    </View>
                  </View>
                </View>
              </View>
              <View style={styles.helpText}>
                <FontAwesome5 name="info-circle" size={14} color="#666" />
                <Text style={styles.helpTextContent}>
                  Specify the buffer or clean time duration in hours and minutes.
                </Text>
              </View>
            </View>
          </View>

          {/* App Configuration Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>App Configuration</Text>
            <View style={styles.coordinatesContainer}>
              <View style={styles.inputRow}>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>App Name:</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={appName}
                      onChangeText={setAppName}
                      placeholder="e.g., bike delivery app"
                      placeholderTextColor="#AAAAAA"
                    />
                  </View>
                </View>
                {/* <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>App Email:</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={appEmail}
                      onChangeText={setAppEmail}
                      placeholder="e.g., order@bodhisys.io"
                      placeholderTextColor="#AAAAAA"
                      keyboardType="email-address"
                      autoCapitalize="none"
                    />
                  </View>
                </View> */}
              </View>

              {/* Stripe Configuration */}
              <View style={[styles.inputRow, { marginTop: 15 }]}>
                <View style={{ width: '100%' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 15 }}>
                    <Text style={[styles.inputLabel, { fontWeight: 'bold' }]}>Enable Live Stripe Keys:</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={{ marginRight: 8, color: '#6c757d', fontWeight: isStripeLiveEnabled ? '400' : '600' }}>TEST</Text>
                      <Switch
                        value={isStripeLiveEnabled}
                        onValueChange={setIsStripeLiveEnabled}
                        trackColor={{ false: '#6c757d', true: '#2e96e1' }}
                        thumbColor={isStripeLiveEnabled ? '#ffc107' : '#f8f9fa'}
                      />
                      <Text style={{ marginLeft: 8, color: '#6c757d', fontWeight: isStripeLiveEnabled ? '600' : '400' }}>LIVE</Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.inputRow}>
                <View style={[styles.inputGroup, { width: '100%', marginBottom: 15 }]}>
                  <Text style={styles.inputLabel}>Stripe Live Public Key:</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={stripeLivePublicKey}
                      onChangeText={setStripeLivePublicKey}
                      placeholder="pk_live_..."
                      placeholderTextColor="#AAAAAA"
                    />
                  </View>
                </View>
              </View>
              <View style={[styles.inputRow, { marginTop: -15, marginBottom: 15 }]}>
                <View style={[styles.inputGroup, { width: '100%' }]}>
                  <Text style={styles.inputLabel}>Stripe Live Secret Key:</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={stripeLiveSecretKey}
                      onChangeText={setStripeLiveSecretKey}
                      placeholder="sk_live_..."
                      placeholderTextColor="#AAAAAA"
                      secureTextEntry={!showLiveSecretKey}
                    />
                    <TouchableOpacity onPress={handleToggleLiveSecret} style={{ padding: 10 }}>
                      <FontAwesome5 name={showLiveSecretKey ? "eye-slash" : "eye"} size={16} color="#666" />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              <View style={styles.inputRow}>
                <View style={[styles.inputGroup, { width: '100%', marginBottom: 15 }]}>
                  <Text style={styles.inputLabel}>Stripe Test Public Key:</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={stripeTestPublicKey}
                      onChangeText={setStripeTestPublicKey}
                      placeholder="pk_test_..."
                      placeholderTextColor="#AAAAAA"
                    />
                  </View>
                </View>
              </View>
              <View style={[styles.inputRow, { marginTop: -15 }]}>
                <View style={[styles.inputGroup, { width: '100%' }]}>
                  <Text style={styles.inputLabel}>Stripe Test Secret Key:</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={stripeTestSecretKey}
                      onChangeText={setStripeTestSecretKey}
                      placeholder="sk_test_..."
                      placeholderTextColor="#AAAAAA"
                      secureTextEntry={!showTestSecretKey}
                    />
                    <TouchableOpacity onPress={handleToggleTestSecret} style={{ padding: 10 }}>
                      <FontAwesome5 name={showTestSecretKey ? "eye-slash" : "eye"} size={16} color="#666" />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
              <View style={styles.helpText}>
                <FontAwesome5 name="info-circle" size={14} color="#666" />
                <Text style={styles.helpTextContent}>
                  Configure the application name and contact email for your business.
                </Text>
              </View>
            </View>
          </View>

          {/* SendGrid API Configuration Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>SendGrid Email Configuration</Text>
            <View style={styles.coordinatesContainer}>
              {/* Enable/Disable Toggle */}
              <View style={[styles.inputRow, { marginBottom: 15 }]}>
                <View style={{ width: '100%' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text style={[styles.inputLabel, { fontWeight: 'bold' }]}>Enable SendGrid:</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={{ marginRight: 8, color: '#6c757d', fontWeight: isSendGridEnabled ? '400' : '600' }}>OFF</Text>
                      <Switch
                        value={isSendGridEnabled}
                        onValueChange={setIsSendGridEnabled}
                        trackColor={{ false: '#6c757d', true: '#2e96e1' }}
                        thumbColor={isSendGridEnabled ? '#ffc107' : '#f8f9fa'}
                      />
                      <Text style={{ marginLeft: 8, color: '#6c757d', fontWeight: isSendGridEnabled ? '600' : '400' }}>ON</Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.inputRow}>
                <View style={[styles.inputGroup, { width: '100%' }]}>
                  <Text style={styles.inputLabel}>App Email (From Address):</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={appEmail}
                      onChangeText={setAppEmail}
                      placeholder="e.g., noreply@yourdomain.com"
                      placeholderTextColor="#AAAAAA"
                      keyboardType="email-address"
                      autoCapitalize="none"
                    />
                  </View>
                </View>
              </View>

              <View style={styles.inputRow}>
                <View style={[styles.inputGroup, { width: '100%' }]}>
                  <Text style={styles.inputLabel}>SendGrid API Key:</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={sendgridApiKey}
                      onChangeText={setSendgridApiKey}
                      placeholder="SG.xxxxxxxxxxxxxxxx"
                      placeholderTextColor="#AAAAAA"
                      secureTextEntry={!showSendgridApiKey}
                      autoCapitalize="none"
                    />
                    <TouchableOpacity onPress={handleToggleSendgridApiKey} style={{ padding: 10 }}>
                      <FontAwesome5 name={showSendgridApiKey ? "eye-slash" : "eye"} size={16} color="#666" />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              <View style={styles.helpText}>
                <FontAwesome5 name="info-circle" size={14} color="#666" />
                <Text style={styles.helpTextContent}>
                  Enter your SendGrid API key to enable email sending via the SendGrid Web API.
                </Text>
              </View>
            </View>
          </View>

          {/* Twilio SMS Configuration Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Twilio SMS Configuration</Text>
            <View style={styles.coordinatesContainer}>
              {/* Enable/Disable Toggle */}
              <View style={[styles.inputRow, { marginBottom: 15 }]}>
                <View style={{ width: '100%' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text style={[styles.inputLabel, { fontWeight: 'bold' }]}>Enable Twilio:</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={{ marginRight: 8, color: '#6c757d', fontWeight: isTwilioEnabled ? '400' : '600' }}>OFF</Text>
                      <Switch
                        value={isTwilioEnabled}
                        onValueChange={setIsTwilioEnabled}
                        trackColor={{ false: '#6c757d', true: '#2e96e1' }}
                        thumbColor={isTwilioEnabled ? '#ffc107' : '#f8f9fa'}
                      />
                      <Text style={{ marginLeft: 8, color: '#6c757d', fontWeight: isTwilioEnabled ? '600' : '400' }}>ON</Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.inputRow}>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Account SID:</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={twilioAccountSid}
                      onChangeText={setTwilioAccountSid}
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      placeholderTextColor="#AAAAAA"
                      autoCapitalize="none"
                    />
                  </View>
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Twilio Phone Number:</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={twilioNumber}
                      onChangeText={setTwilioNumber}
                      placeholder="+1234567890"
                      placeholderTextColor="#AAAAAA"
                      keyboardType="phone-pad"
                    />
                  </View>
                </View>
              </View>

              <View style={[styles.inputRow, { marginTop: -15 }]}>
                <View style={[styles.inputGroup, { width: '100%' }]}>
                  <Text style={styles.inputLabel}>Auth Token:</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={styles.input}
                      value={twilioAuthToken}
                      onChangeText={setTwilioAuthToken}
                      placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      placeholderTextColor="#AAAAAA"
                      secureTextEntry={!showTwilioAuthToken}
                      autoCapitalize="none"
                    />
                    <TouchableOpacity onPress={handleToggleTwilioAuthToken} style={{ padding: 10 }}>
                      <FontAwesome5 name={showTwilioAuthToken ? "eye-slash" : "eye"} size={16} color="#666" />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              <View style={styles.helpText}>
                <FontAwesome5 name="info-circle" size={14} color="#666" />
                <Text style={styles.helpTextContent}>
                  Configure Twilio settings for sending SMS notifications. Get your credentials from Twilio Console.
                </Text>
              </View>
            </View>
          </View>

          {/* New Order Alerts Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>New Order Alerts</Text>
            <View style={styles.coordinatesContainer}>
              {/* Enable/Disable Email Alert Toggle */}
              <View style={[styles.inputRow, { marginBottom: 15 }]}>
                <View style={{ width: '100%' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text style={[styles.inputLabel, { fontWeight: 'bold' }]}>Receive email alert on new order:</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={{ marginRight: 8, color: '#6c757d', fontWeight: isNewOrderEmailAlertEnabled ? '400' : '600' }}>OFF</Text>
                      <Switch
                        value={isNewOrderEmailAlertEnabled}
                        onValueChange={setIsNewOrderEmailAlertEnabled}
                        trackColor={{ false: '#6c757d', true: '#2e96e1' }}
                        thumbColor={isNewOrderEmailAlertEnabled ? '#ffc107' : '#f8f9fa'}
                      />
                      <Text style={{ marginLeft: 8, color: '#6c757d', fontWeight: isNewOrderEmailAlertEnabled ? '600' : '400' }}>ON</Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.inputRow}>
                <View style={[styles.inputGroup, { width: '100%' }]}>
                  <Text style={styles.inputLabel}>Alert email recipient(s):</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={[styles.input, !isNewOrderEmailAlertEnabled && { backgroundColor: '#f0f0f0' }]}
                      value={newOrderEmailAlertRecipients}
                      onChangeText={setNewOrderEmailAlertRecipients}
                      placeholder="e.g., orders@domain.com, alerts@domain.com"
                      placeholderTextColor="#AAAAAA"
                      keyboardType="email-address"
                      autoCapitalize="none"
                      editable={isNewOrderEmailAlertEnabled}
                    />
                  </View>
                </View>
              </View>

              {/* Enable/Disable SMS Alert Toggle */}
              <View style={[styles.inputRow, { marginBottom: 15, marginTop: 15 }]}>
                <View style={{ width: '100%' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text style={[styles.inputLabel, { fontWeight: 'bold' }]}>Receive SMS alert on new order:</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={{ marginRight: 8, color: '#6c757d', fontWeight: isNewOrderSmsAlertEnabled ? '400' : '600' }}>OFF</Text>
                      <Switch
                        value={isNewOrderSmsAlertEnabled}
                        onValueChange={setIsNewOrderSmsAlertEnabled}
                        trackColor={{ false: '#6c757d', true: '#2e96e1' }}
                        thumbColor={isNewOrderSmsAlertEnabled ? '#ffc107' : '#f8f9fa'}
                      />
                      <Text style={{ marginLeft: 8, color: '#6c757d', fontWeight: isNewOrderSmsAlertEnabled ? '600' : '400' }}>ON</Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.inputRow}>
                <View style={[styles.inputGroup, { width: '100%' }]}>
                  <Text style={styles.inputLabel}>Alert SMS recipient(s):</Text>
                  <View style={styles.inputWithIcon}>
                    <TextInput
                      style={[styles.input, !isNewOrderSmsAlertEnabled && { backgroundColor: '#f0f0f0' }]}
                      value={newOrderSmsAlertRecipients}
                      onChangeText={setNewOrderSmsAlertRecipients}
                      placeholder="e.g., +1234567890, +0987654321"
                      placeholderTextColor="#AAAAAA"
                      keyboardType="phone-pad"
                      editable={isNewOrderSmsAlertEnabled}
                    />
                  </View>
                </View>
              </View>

              {/* Brand filter for new order alerts */}
              <View style={[styles.inputRow, { marginTop: 15 }]}>
                <View style={{ width: '100%' }}>
                  <Text style={[styles.inputLabel, { fontWeight: 'bold', marginBottom: 8 }]}>
                    Brands to alert (leave empty for all):
                  </Text>
                  <View style={{ flexDirection: 'row', marginBottom: 8 }}>
                    <TouchableOpacity onPress={() => setSelectedAlertBrandIds(brandsList.map((b) => Number(b.id)))}>
                      <Text style={{ color: '#2e96e1', marginRight: 16 }}>Select All</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => setSelectedAlertBrandIds([])}>
                      <Text style={{ color: '#2e96e1' }}>Clear</Text>
                    </TouchableOpacity>
                  </View>
                  <View style={{ maxHeight: 150, borderWidth: 1, borderColor: '#ddd', borderRadius: 5, padding: 8 }}>
                    <ScrollView>
                      {brandsList.map((brand) => {
                        const brandId = Number(brand.id);
                        const isSelected = selectedAlertBrandIds.includes(brandId);
                        return (
                          <TouchableOpacity
                            key={brandId}
                            onPress={() => toggleAlertBrand(brandId)}
                            style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6 }}
                          >
                            <FontAwesome5
                              name={isSelected ? 'check-square' : 'square'}
                              size={16}
                              color={isSelected ? '#2e96e1' : '#6c757d'}
                              style={{ marginRight: 10 }}
                            />
                            <Text style={{ fontSize: 15, color: '#333' }}>
                              {brand.brand || `Brand #${brandId}`}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                      {brandsList.length === 0 && (
                        <Text style={{ color: '#6c757d', fontStyle: 'italic' }}>No brands available</Text>
                      )}
                    </ScrollView>
                  </View>
                </View>
              </View>

              <View style={styles.helpText}>
                <FontAwesome5 name="info-circle" size={14} color="#666" />
                <Text style={styles.helpTextContent}>
                  When enabled, an email and/or SMS will be sent to the configured recipients whenever a new order is created. If no email recipients are set, the alert is sent to the App Email address.
                </Text>
              </View>
            </View>
          </View>

          {/* Tracking Tags Toggle */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Tracking Tags</Text>
            <View style={{ width: '100%' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={styles.inputLabel}>Enable tracking tags:</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={{ marginRight: 8, color: '#6c757d', fontWeight: isTrackingTagsEnabled ? '400' : '600' }}>OFF</Text>
                  <Switch
                    value={isTrackingTagsEnabled}
                    onValueChange={(newValue) => setIsTrackingTagsEnabled(newValue)}
                    trackColor={{ false: '#6c757d', true: '#2e96e1' }}
                    thumbColor={isTrackingTagsEnabled ? '#ffc107' : '#f8f9fa'}
                  />
                  <Text style={{ marginLeft: 8, color: '#6c757d', fontWeight: isTrackingTagsEnabled ? '600' : '400' }}>ON</Text>
                </View>
              </View>
              <View style={styles.helpText}>
                <FontAwesome5 name="info-circle" size={14} color="#666" />
                <Text style={styles.helpTextContent}>
                  Toggle to enable/disable tracking tags functionality.
                </Text>
              </View>
            </View>
          </View>

          {/* Allow Reservations Brand Change Toggle */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Allow Reservations Brand Change</Text>
            <View style={{ width: '100%' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={styles.inputLabel}>Allow changing the brand on a reservation:</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={{ marginRight: 8, color: '#6c757d', fontWeight: isBrandChangeEnabled ? '400' : '600' }}>OFF</Text>
                  <Switch
                    value={isBrandChangeEnabled}
                    onValueChange={(newValue) => setIsBrandChangeEnabled(newValue)}
                    trackColor={{ false: '#6c757d', true: '#2e96e1' }}
                    thumbColor={isBrandChangeEnabled ? '#ffc107' : '#f8f9fa'}
                  />
                  <Text style={{ marginLeft: 8, color: '#6c757d', fontWeight: isBrandChangeEnabled ? '600' : '400' }}>ON</Text>
                </View>
              </View>
              <View style={styles.helpText}>
                <FontAwesome5 name="info-circle" size={14} color="#666" />
                <Text style={styles.helpTextContent}>
                  When enabled, the Brand field on the reservation detail screen becomes an editable dropdown.
                </Text>
              </View>
            </View>
          </View>

          {/* Save Profile Button */}
          <View style={styles.saveButtonContainer}>
            <BOHButton
              label={isLoading ? 'Saving...' : 'Save Profile'}
              onPress={saveProfile}
              disabled={isLoading}
            />
          </View>
        </ScrollView>
      </CommonContainer>
    </BasicLayout>
  );
};

const styles = StyleSheet.create({
  section: {
    marginBottom: 30,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingBottom: 20,
    width: '100%',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 15,
    color: '#333',
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  logo: {
    width: 120,
    height: 120,
    marginRight: 30,
    borderRadius: 8,
    objectFit: 'contain',
  },
  placeholderLogo: {
    width: 120,
    height: 120,
    marginRight: 30,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#ddd',
    borderStyle: 'dashed',
  },
  coordinatesContainer: {
    width: '100%',
  },
  inputRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  inputGroup: {
    width: '48%',
  },
  inputLabel: {
    fontSize: 16,
    marginBottom: 5,
    color: '#444',
  },
  inputWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 5,
    overflow: 'hidden',
  },
  input: {
    flex: 1,
    height: 40,
    paddingHorizontal: 10,
    fontSize: 16,
  },
  inputUnit: {
    paddingHorizontal: 10,
    paddingEnd: 20,
    backgroundColor: '#f0f0f0',
    height: 40,
    lineHeight: 40,
    color: '#666',
  },
  helpText: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  helpTextContent: {
    marginLeft: 5,
    fontSize: 14,
    color: '#666',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
  },
  saveButtonContainer: {
    marginTop: 20,
    alignItems: 'flex-end',
  },
  closingTimeContainer: {
    width: '100%',
  },
});

export default GeneralSettings;
